import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { Card, CardContent } from '@/shared/components/ui/card'
import { cn } from '@/shared/lib/utils'
import type { AccessJustificatif } from '@/shared/schemas/accessJustificatif.schema'
import { ServiceStatusBadge, type ServiceStatusBadgeTone } from './ServiceStatusBadge'

const toneByStatus: Record<'missing' | 'pending' | 'approved' | 'rejected', ServiceStatusBadgeTone> = {
  missing: 'closed',
  pending: 'pending',
  approved: 'open',
  rejected: 'closed',
}

/**
 * T6.2: gate notice shown on a property/offer detail page when access requires an admin-approved
 * justificatif. Renders nothing when the resource isn't gated. RGAA: status carried by
 * icon+text+color (ServiceStatusBadge), not color alone.
 */
export function AccessJustificatifNotice({
  gate,
  className,
}: {
  gate: AccessJustificatif | null | undefined
  className?: string
}) {
  const { t } = useTranslation()

  if (!gate?.required) {
    return null
  }

  const status = gate.user_document_status ?? 'missing'
  if (status === 'approved') {
    return null
  }

  return (
    <Card className={cn('border-warning/40 bg-warning/5', className)}>
      <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1.5">
          <ServiceStatusBadge tone={toneByStatus[status]} label={t(`shared.access_justificatif.status.${status}`)} />
          <p className="text-sm text-muted-foreground">
            {t('shared.access_justificatif.explanation', { docType: gate.doc_type ?? '' })}
          </p>
        </div>
        <Link
          to="/me"
          className="inline-flex shrink-0 items-center justify-center rounded-md border border-primary px-3 py-2 text-sm font-medium text-primary hover:bg-primary/10"
        >
          {t('shared.access_justificatif.cta')}
        </Link>
      </CardContent>
    </Card>
  )
}
